// File: /pages/Choreography.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import Link from 'next/link';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
//import styles from './Choreography.module.css'; // Optional: additional CSS for this page

// Helper component to render a single Choreography item using a tabbed layout.
const ChoreographyContentDisplay = ({ choreography }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  // --- Overview Tab ---
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p><strong>Status:</strong> {choreography.status || 'N/A'}</p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {choreography.submission_date
              ? new Date(choreography.submission_date).toLocaleDateString()
              : 'N/A'}
          </p>
          {choreography.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(choreography.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {choreography.created_at
              ? new Date(choreography.created_at).toLocaleDateString()
              : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {choreography.updated_at
              ? new Date(choreography.updated_at).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
        {/* Right Column: Perspective Carousel and DMCA Link */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={choreography} />
          <div className={displayStyles.dmcaLinkContainer}>
            <Link
              href={`/DMCA?contentHash=${choreography.content_id}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {choreography.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={choreography.content_id} />
    </div>
  );

  // --- Classification Tab ---
  const classificationTabContent = (
    <ClassificationDisplay content={choreography} />
  );

  // --- Media Tab ---
  const galleryImages = Array.isArray(choreography.gallery_images)
    ? choreography.gallery_images
    : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={choreography} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={choreography} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

const ChoreographyPage = () => {
  const [choreographyItems, setChoreographyItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch parent category mapping.
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch all content items and then filter for type "Choreography".
  useEffect(() => {
    const fetchChoreography = async () => {
      try {
        const res = await fetch('/api/content/content');
        if (!res.ok) throw new Error('Failed to fetch content');
        const data = await res.json();
        let items = Array.isArray(data.content) ? data.content : [];
        // Filter items by type "Choreography" (case-insensitive)
        items = items.filter(
          (item) =>
            item.type &&
            item.type.trim().toLowerCase() === 'choreography'
        );
        setChoreographyItems(items);
      } catch (err) {
        console.error('Error fetching choreography:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchChoreography();
  }, []);

  if (loading) return <p>Loading choreography...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group choreography items by parent_category_id.
  const groups = {};
  choreographyItems.forEach((item) => {
    let parentCategories = [];
    if (item.parent_category_id) {
      try {
        parentCategories = JSON.parse(item.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        parentCategories = item.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(item);
    } else {
      parentCategories.forEach((cat) => {
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    }
  });

  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((item) => ({
        header: item.name,
        content: <ChoreographyContentDisplay choreography={item} />,
      })),
    };
  });

  return (
    <section id="choreography" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Choreography - Express Your Style:</h1>
        <h2>
          Explore our collection of shuffle dance choreographies designed to inspire and challenge you. Click on an accordion header to view the choreography details.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No choreography found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Choreography" />
      </div>
    </section>
  );
};

export default ChoreographyPage;
